import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaUser,
  FaRocket,
  FaShoppingBag,
  FaClipboardList,
  FaSignOutAlt,
} from "react-icons/fa";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { useAuth } from "../../context/AuthContext";
import { getOrdersForUser } from "../../utils/orderStorage";

const Profile = () => {
  const { t } = useTranslation(["member", "common"]);
  const { user, logout } = useAuth();

  const orders = useMemo(
    () => (user ? getOrdersForUser(user.id) : []),
    [user]
  );

  const totalSpent = orders.reduce((sum, order) => sum + order.subtotal, 0);
  const recentOrders = orders.slice(0, 3);

  const displayName =
    user?.firstName || user?.lastName
      ? [user.firstName, user.lastName].filter(Boolean).join(" ")
      : user?.email?.split("@")[0] ?? "";

  const avatarLetter =
    displayName.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || "U";

  return (
    <Layout title={t("member:page_title")}>
      <div className="max-w-5xl mx-auto py-4 md:py-8">
        <div className="text-center mb-10 md:mb-12">
          <p className="text-caption text-neon-green mb-3">港微創 · Member</p>
          <h1
            className="text-h1 md:text-display text-gradient-brand mb-4"
            style={{ lineHeight: "125%" }}
          >
            {t("member:page_title")}
          </h1>
          <p
            className="text-body-lg text-gray-400 max-w-2xl mx-auto"
            style={{ lineHeight: "125%" }}
          >
            {t("member:page_subtitle")}
          </p>
        </div>

        <div className="rounded-2xl border border-neon-green/30 bg-neon-green/5 p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="shrink-0 w-16 h-16 rounded-full bg-neon-green/10 border-2 border-neon-green/40 flex items-center justify-center">
              <span className="text-h2 font-bold text-neon-green">
                {avatarLetter}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-body-sm text-gray-400 mb-1">
                {t("member:welcome")}
              </p>
              <h2 className="text-h2 text-white truncate">{displayName}</h2>
              <p className="text-body-sm text-gray-400 truncate mt-1">
                {user?.email}
              </p>
            </div>
            <span className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full text-caption bg-space/60 text-neon-green border border-neon-green/30">
              <FaUser size={12} />
              {t("member:member_badge")}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
            <div className="rounded-xl bg-space/40 border border-white/10 p-4 text-center">
              <p className="text-caption text-gray-500 mb-1">
                {t("member:stat_orders")}
              </p>
              <p className="text-h2 text-white tabular-nums">{orders.length}</p>
            </div>
            <div className="rounded-xl bg-space/40 border border-white/10 p-4 text-center">
              <p className="text-caption text-gray-500 mb-1">
                {t("member:stat_spent")}
              </p>
              <p className="text-h2 text-neon-green tabular-nums">
                HKD {totalSpent.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <h3 className="text-h4 text-white mb-4">{t("member:quick_actions")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-neon-green/40 bg-gradient-to-br from-neon-green/10 to-neon-blue/5 p-5 flex flex-col">
            <div className="w-10 h-10 rounded-lg bg-neon-green/10 border border-neon-green/30 flex items-center justify-center mb-4">
              <FaRocket className="text-neon-green" />
            </div>
            <h4 className="text-body font-semibold text-white mb-2">
              {t("member:workbench_title")}
            </h4>
            <p className="text-body-sm text-gray-400 mb-4 flex-1">
              {t("member:workbench_desc")}
            </p>
            <Button
              size="sm"
              disabled
              className="w-full bg-gradient-brand text-space opacity-60 cursor-not-allowed"
            >
              {t("member:workbench_cta")} · {t("member:workbench_soon")}
            </Button>
          </div>

          <Link
            to="/products"
            className="rounded-2xl border border-white/10 bg-space-light/50 p-5 flex flex-col hover:border-neon-green/40 transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4 group-hover:border-neon-green/30">
              <FaShoppingBag className="text-gray-300 group-hover:text-neon-green transition-colors" />
            </div>
            <h4 className="text-body font-semibold text-white mb-2">
              {t("member:browse_title")}
            </h4>
            <p className="text-body-sm text-gray-400 mb-4 flex-1">
              {t("member:browse_desc")}
            </p>
            <span className="text-body-sm text-neon-green font-medium">
              {t("member:browse_cta")} →
            </span>
          </Link>

          <Link
            to="/orders"
            className="rounded-2xl border border-white/10 bg-space-light/50 p-5 flex flex-col hover:border-neon-green/40 transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4 group-hover:border-neon-green/30">
              <FaClipboardList className="text-gray-300 group-hover:text-neon-green transition-colors" />
            </div>
            <h4 className="text-body font-semibold text-white mb-2">
              {t("member:orders_title")}
            </h4>
            <p className="text-body-sm text-gray-400 mb-4 flex-1">
              {t("member:orders_desc")}
            </p>
            <span className="text-body-sm text-neon-green font-medium">
              {t("member:orders_cta")} →
            </span>
          </Link>
        </div>

        <section className="rounded-2xl border border-white/10 bg-space-light/50 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-h4 text-white">{t("member:recent_orders")}</h3>
            {orders.length > 0 && (
              <Link
                to="/orders"
                className="text-body-sm text-neon-green hover:underline"
              >
                {t("member:orders_cta")}
              </Link>
            )}
          </div>

          {recentOrders.length > 0 ? (
            <ul className="space-y-3">
              {recentOrders.map((order) => (
                <li
                  key={order.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4 rounded-xl bg-space/40 border border-white/10"
                >
                  <div>
                    <p className="text-body-sm text-neon-green font-medium tabular-nums">
                      {order.id}
                    </p>
                    <p className="text-caption text-gray-500 mt-0.5">
                      {new Date(order.createdAt).toLocaleString("zh-HK")}
                    </p>
                  </div>
                  <p className="text-body font-bold text-white tabular-nums">
                    HKD {order.subtotal.toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-body-sm text-gray-400 py-4 text-center">
              {t("member:no_recent_orders")}
            </p>
          )}
        </section>

        <section className="rounded-2xl border border-white/10 bg-space-light/50 p-6">
          <h3 className="text-h4 text-white mb-1">{t("member:account_section")}</h3>
          <p className="text-body-sm text-gray-400 mb-5">
            {t("member:account_hint")}
          </p>
          <div className="max-w-md">
            <label className="block text-body-sm font-medium text-gray-300 mb-1.5">
              {t("common:email")}
            </label>
            <input
              type="email"
              value={user?.email ?? ""}
              disabled
              className="w-full px-4 py-2.5 rounded-lg border border-white/20 bg-space/50 text-gray-400 text-body-sm"
            />
          </div>
          <div className="mt-6 pt-6 border-t border-white/10">
            <Button
              variant="outline"
              onClick={logout}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
            >
              <FaSignOutAlt className="mr-2" />
              {t("member:logout")}
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Profile;
